import React, { useCallback, useEffect, useState } from "react";
import {
  BarChart3, Phone, MessageSquare, UserPlus, Target, CalendarCheck,
  PhoneForwarded, StickyNote, AlertTriangle, MoonStar, DollarSign, Download,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { PageHeader, MetricCard, Select, Button, LoadingBlock, EmptyState, Checkbox, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { AttributionPanel } from "./AttributionPanel";
import { ForecastsPanel } from "./ForecastsPanel";

const DATE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "this_month", label: "This month" },
  { value: "custom", label: "Custom range" },
];

const PIE_COLORS = ["#2563eb", "#0ea5e9", "#14b8a6", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#64748b"];

const money = (n: number | null | undefined) => (n == null ? "$0" : `$${Number(n).toLocaleString()}`);

const METRIC_DEFS: { key: string; label: string; icon: any; hint?: string; money?: boolean }[] = [
  { key: "calls_answered", label: "Calls answered", icon: Phone },
  { key: "sms_conversations", label: "SMS conversations", icon: MessageSquare },
  { key: "new_contacts", label: "New contacts", icon: UserPlus },
  { key: "new_leads", label: "New leads", icon: Target },
  { key: "qualified_leads", label: "Qualified leads", icon: Target },
  { key: "appointments_booked", label: "Appointments booked", icon: CalendarCheck },
  { key: "human_transfers", label: "Human transfers", icon: PhoneForwarded },
  { key: "messages_taken", label: "Messages taken", icon: StickyNote },
  { key: "failed_interactions", label: "Failed interactions", icon: AlertTriangle },
  { key: "after_hours_opportunities", label: "After-hours opportunities", icon: MoonStar },
];

function ChartCard({ title, subtitle, children, empty }: { title: string; subtitle?: string; children: React.ReactNode; empty: boolean }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`chart-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
      </div>
      {empty ? (
        <div className="flex h-[220px] items-center justify-center text-sm text-slate-400">No data in this range.</div>
      ) : (
        <div style={{ width: "100%", minWidth: 0, height: 220 }}>{children}</div>
      )}
    </div>
  );
}

const tidyLabel = (s: any) => String(s ?? "unknown").replace(/_/g, " ");
const shortLabel = (s: any) => { const t = tidyLabel(s); return t.length > 18 ? `${t.slice(0, 17)}…` : t; };

export default function Analytics() {
  const [dateFilter, setDateFilter] = useState("last_30_days");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");
  const [digest, setDigest] = useState<{ enabled: boolean; last_sent_at: string | null; email_configured: boolean } | null>(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  useEffect(() => { api.analyticsDigestGet().then(setDigest).catch(() => {}); }, []);

  const toggleDigest = async (v: boolean) => {
    setDigest((d) => (d ? { ...d, enabled: v } : d));
    try { await api.analyticsDigestSet(v); toast.success(v ? "Weekly digest on" : "Weekly digest off"); }
    catch (e) { toast.error(formatApiError(e)); api.analyticsDigestGet().then(setDigest).catch(() => {}); }
  };
  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const r = await api.analyticsDigestSendNow();
      toast.success(r.sent ? `Weekly summary sent to ${r.to}` : (r.simulated ? "Email not configured — nothing sent" : `Not sent: ${r.reason}`));
      api.analyticsDigestGet().then(setDigest).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingDigest(false); }
  };

  const params = useCallback(() => ({
    date_filter: dateFilter,
    ...(dateFilter === "custom" ? { custom_from: customFrom, custom_to: customTo } : {}),
  }), [dateFilter, customFrom, customTo]);

  const load = useCallback(async () => {
    if (dateFilter === "custom" && (!customFrom || !customTo)) return;
    setLoading(true);
    try {
      setData(await api.analyticsOverview(params()));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [dateFilter, customFrom, customTo, params]);

  useEffect(() => { load(); }, [load]);

  const doExport = async (kind: string) => {
    setExporting(kind);
    try {
      await api.analyticsExport(kind, params());
      toast.success(`${kind} export downloaded`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting("");
    }
  };

  const metrics = data?.metrics ?? {};
  const charts = data?.charts ?? {};

  const barData = (arr: any[]) => (arr ?? []).map((r) => ({ name: tidyLabel(r.label), count: r.count }));

  return (
    <div data-testid="page-analytics" className="space-y-6">
      <PageHeader
        title="Analytics"
        testid="analytics-header"
        description="Outcome trends from your AI front desk. All figures are for your organization only."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={dateFilter} onChange={setDateFilter} options={DATE_OPTIONS} className="w-[170px]" testid="analytics-date-filter" />
            {dateFilter === "custom" && (
              <>
                <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                  className="h-9 rounded-md border border-slate-300 px-2 text-sm" data-testid="analytics-custom-from" />
                <span className="text-slate-400">–</span>
                <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                  className="h-9 rounded-md border border-slate-300 px-2 text-sm" data-testid="analytics-custom-to" />
              </>
            )}
          </div>
        }
      />

      {/* Weekly email digest */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3" data-testid="analytics-digest-bar">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Checkbox checked={!!digest?.enabled} onChange={(v: boolean) => toggleDigest(!!v)} testid="analytics-digest-toggle" />
          <span>Email me a <b>weekly summary</b> of these numbers{digest?.last_sent_at ? ` · last sent ${new Date(digest.last_sent_at).toLocaleDateString()}` : ""}</span>
        </label>
        <Button size="sm" variant="outline" onClick={sendDigestNow} disabled={sendingDigest} data-testid="analytics-digest-send-now">
          {sendingDigest ? "Sending…" : "Send me this week's summary now"}
        </Button>
      </div>

      <AttributionPanel params={params()} />

      <ForecastsPanel />


      {/* Export bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="analytics-export-bar">
        <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <Download className="h-4 w-4" /> Export CSV
        </span>
        {["leads", "appointments", "calls", "usage"].map((k) => (
          <Button key={k} size="sm" variant="outline" onClick={() => doExport(k)}
            disabled={!!exporting} data-testid={`analytics-export-${k}`}>
            {exporting === k ? "Preparing…" : `${k[0].toUpperCase()}${k.slice(1)}`}
          </Button>
        ))}
      </div>

      {loading && !data ? (
        <LoadingBlock rows={6} />
      ) : (
        <>
          {/* Metric cards */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5" data-testid="analytics-metrics">
            {METRIC_DEFS.map((m) => (
              <MetricCard key={m.key} label={m.label} icon={m.icon}
                value={metrics[m.key] ?? 0} testid={`metric-${m.key}`} />
            ))}
            <MetricCard label="Est. pipeline value" icon={DollarSign}
              value={money(metrics.estimated_pipeline_value)}
              hint={metrics.estimated_pipeline_note} testid="metric-estimated_pipeline_value"
              className="col-span-2 md:col-span-1" />
          </div>

          {/* Charts */}
          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard title="Calls by outcome" empty={!barData(charts.calls_by_outcome).length}>
              <ResponsiveContainer>
                <BarChart data={barData(charts.calls_by_outcome)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Appointments over time" empty={!barData(charts.appointments_over_time).length}>
              <ResponsiveContainer>
                <LineChart data={barData(charts.appointments_over_time)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#14b8a6" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Leads by source" empty={!barData(charts.leads_by_source).length}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={barData(charts.leads_by_source)} dataKey="count" nameKey="name"
                    cx="50%" cy="50%" outerRadius={80} label={(e: any) => shortLabel(e.name)}>
                    {barData(charts.leads_by_source).map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Most requested services" empty={!barData(charts.most_requested_services).length}>
              <ResponsiveContainer>
                <BarChart data={barData(charts.most_requested_services)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Calls by hour of day" subtitle="Your organization timezone" empty={!(charts.calls_by_hour ?? []).some((r: any) => r.count > 0)}>
              <ResponsiveContainer>
                <BarChart data={barData(charts.calls_by_hour)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="name" interval={2} tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Escalation reasons" empty={!barData(charts.escalation_reasons).length}>
              <ResponsiveContainer>
                <BarChart data={barData(charts.escalation_reasons)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {data && Object.values(metrics).every((v) => !v) && (
            <EmptyState
              icon={BarChart3}
              title="No activity yet"
              description="As calls, SMS, leads and appointments come in, your analytics will populate here automatically."
            />
          )}
        </>
      )}
    </div>
  );
}
